/* eslint-disable max-len */
import React from 'react';

import { useParams } from 'react-router-dom';

import Record from 'ui/Record/Record';

import { DefaultFeedMapping } from 'ui/Feed/Feed';

import { CenteredCircularProgress } from 'components/Progress';

import { formatMoment } from 'utils/formatters/timezone';

import { isNotFound } from 'utils/api/errors';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';

import EntityDetailsContent from 'components/Entities/EntityDetailsContent';

import EntityDetailsHeader from 'components/Entities/EntityDetailsHeader';
import EntityDetailsActivities from 'components/Entities/EntityDetailsActivities';
import EntityDetailsActivitiesFooter from 'components/Entities/EntityDetailsActivities/EntityDetailsActivitiesFooter';

import { ActivityMapping } from 'utils/api/types';
import EntityRelatedActionsCell from 'components/SharedFormComponents/EntityRelatedActionsCell';
import DataError from 'containers/Error/DataError';
import NotFound from 'containers/Error/Pages/NotFound/NotFound';
import EntitiesDetailsLoader from 'ui/DetailsLoader/EntitiesDetailsLoader';

import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR } from 'core/constants';

const activityTypeToComponent = {
  ...DefaultFeedMapping,
} as unknown as ActivityMapping;

const AssetsDetails: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const params = useParams<{ entityId: string }>();

  const { assetDetails } = useStore();

  return (
    <EntitiesDetailsLoader params={params} routeParam="entityId" store={assetDetails}>
      {({ isLoaded, error, reload }): React.ReactElement => {
        if (!isLoaded) {
          return <CenteredCircularProgress />;
        }
        if (assetDetails.details === undefined) {
          return <NotFound />;
        }
        if (error) {
          if (isNotFound(error)) {
            return <NotFound />;
          }
          return <DataError onReload={reload} />;
        }

        const assetName = assetDetails.getName(assetDetails.details);

        return (
          <Record
            navBarHeight={navBarHeight}
            Header={
              <EntityDetailsHeader
                store={assetDetails}
                title={assetName}
                columns={[
                  { name: 'Name', value: <p>{assetName}</p> },
                  {
                    name: 'Created',
                    value: (
                      <p data-testid="entity-header-created-at">
                        {formatMoment(assetDetails.details.createdAt, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
                      </p>
                    ),
                  },
                  {
                    name: 'Related Actions',
                    value: (
                      <EntityRelatedActionsCell
                        relatedActions={assetDetails.details.relatedActions || { total: 0, completed: 0 }}
                      />
                    ),
                  },
                ]}
              />
            }
            Content={(onError) => <EntityDetailsContent onError={onError} store={assetDetails} />}
            Activities={{
              Content: (
                <EntityDetailsActivities
                  activities={assetDetails.activities}
                  activityTypeToComponent={activityTypeToComponent}
                />
              ),
              Footer: <EntityDetailsActivitiesFooter activities={assetDetails.activities} />,
            }}
          />
        );
      }}
    </EntitiesDetailsLoader>
  );
};

export default AssetsDetails;
